import { useState, type FormEvent } from "react";
import { useSeo } from "@/hooks/useSeo";

const FORM_ADDR = "https://readdy.ai/api/form/db78nmv799gd4v2fed70";

const feedbackTypes = [
  "内容纠错",
  "资料补充",
  "功能建议",
  "合作咨询",
  "其他反馈",
];

export default function About() {
  useSeo(
    "关于我们 - 葵司影视资讯",
    "了解葵司影视资讯与演员资料大全的定位、发展背景与联系方式，并通过反馈表单与我们取得联系。",
    "葵司,关于我们,影视资讯,演员资料,联系方式",
  );

  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">(
    "idle",
  );
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      params.append(key, String(value));
    });

    setStatus("sending");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam = String(serverMsg ?? "").toLowerCase().includes("spam");
      const isOk =
        response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div>
      <section className="bg-primary-950 text-background-50">
        <div className="max-w-content mx-auto px-4 md:px-6 py-12 md:py-16">
          <span className="inline-flex items-center gap-2 px-3 h-7 rounded-full bg-primary-800 text-background-200 text-xs">
            <i className="ri-information-line" />
            关于葵司
          </span>
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl">
            影视资讯与演员资料大全
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-background-200">
            葵司是一个综合影视内容站，面向影视爱好者、行业从业者与普通观众，提供影视资讯、演员资料及影视作品动态的查询与浏览服务。
          </p>
        </div>
      </section>

      <section id="intro" className="max-w-content mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <article className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-flag-line text-xl" />
            </span>
            <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">
              网站简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              葵司以“信息丰富、条理清晰”为核心，内容涵盖电影、电视剧、综艺等多种类型，并为演员建立完整资料档案，帮助用户高效检索与深度浏览。
            </p>
          </article>

          <article className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-history-line text-xl" />
            </span>
            <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">
              发展背景
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们观察到影视信息分散、查询成本高的问题，于是整合作品资料、演员信息与最新动态，打造一站式的影视内容入口，并持续优化检索与推荐体验。
            </p>
          </article>

          <article id="contact" className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-customer-service-2-line text-xl" />
            </span>
            <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">
              联系方式
            </h2>
            <ul className="mt-3 space-y-2 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-foreground-400" />
                合作与反馈：contact@kuisi-demo.com
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-foreground-400" />
                服务时间：工作日 9:00 - 18:00
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-map-pin-line text-foreground-400" />
                内容运营中心：中国 · 上海
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section id="feedback" className="bg-background-100 border-y border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8 lg:gap-12">
            <div>
              <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950">
                用户反馈
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你发现内容错误、希望补充资料，或对产品有任何建议，欢迎通过下方表单告诉我们。你的每一条反馈，都会帮助葵司变得更好。
              </p>
              <ul className="mt-5 space-y-3 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-600 mt-0.5" />
                  内容纠错与资料补充优先处理
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-600 mt-0.5" />
                  合作咨询请留下有效联系方式
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-600 mt-0.5" />
                  我们会在 3 个工作日内回复
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <form
                data-readdy-form
                id="kuisi-feedback-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="fb-name"
                      className="block text-sm text-foreground-700 mb-2"
                    >
                      你的称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入称呼"
                      className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="fb-email"
                      className="block text-sm text-foreground-700 mb-2"
                    >
                      联系邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="fb-type"
                    className="block text-sm text-foreground-700 mb-2"
                  >
                    反馈类型
                  </label>
                  <select
                    id="fb-type"
                    name="feedback_type"
                    defaultValue={feedbackTypes[0]}
                    className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
                  >
                    {feedbackTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="fb-message"
                    className="block text-sm text-foreground-700 mb-2"
                  >
                    反馈内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你遇到的问题或建议（最多 500 字）"
                    className="w-full px-3 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-y"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="kuisi-extra-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="h-11 px-6 flex items-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className="ri-send-plane-line" />
                    {status === "sending" ? "提交中…" : "提交反馈"}
                  </button>
                  {status === "success" && (
                    <span className="flex items-center gap-1 text-sm text-accent-600">
                      <i className="ri-checkbox-circle-fill" />
                      提交成功，感谢你的反馈！
                    </span>
                  )}
                  {status === "error" && (
                    <span className="flex items-center gap-1 text-sm text-red-600">
                      <i className="ri-error-warning-fill" />
                      {formError}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-600" />
              版权声明
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站所展示的影视作品与演员资料均为演示用途的虚构内容，仅用于产品功能展示，不代表任何真实作品或个人。站内图文若涉及版权问题，请与我们联系，我们将及时处理。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
              <i className="ri-shield-check-line text-primary-600" />
              免责声明
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站不提供任何影视作品的在线播放或下载服务，所有内容仅作为信息展示与检索用途。用户应自行判断信息准确性，因使用本站信息造成的任何损失，本站不承担相应责任。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}